import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { formIdsForStep, formNameById, type FunnelStep } from "./forms";

/**
 * Post-submit confirmation. The public page that only renders after a form
 * submit calls this; we then verify against Agentmoves that a lead really
 * arrived in the last few minutes. Confirmed leads are stored (feeding the
 * admin inbox) and recorded as a form_submit analytics event.
 */

const STEPS = ["visibility_audit", "round_table", "strategy_session", "contact", "blog"] as const;

/** How far back a lead may have been created and still match this page view. */
const WINDOW_MS = 15 * 60 * 1000;
/** Tighter window for the audit widget, which matches on contacts, not forms. */
const CONTACT_WINDOW_MS = 10 * 60 * 1000;

type Candidate = {
  ghlId: string;
  formId: string;
  formName: string;
  name: string;
  email: string;
  phone: string;
  submittedAt: string;
};

export type ConfirmResult = { confirmed: boolean; reason?: string };

export const confirmFormSubmission = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        step: z.enum(STEPS),
        path: z.string().max(500).default("/"),
        sessionId: z.string().max(100).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<ConfirmResult> => {
    const step = data.step as FunnelStep;
    const { fetchFormSubmissions, recentContacts, formsCredentials } = await import("./ghl-forms.server");
    if (!formsCredentials()) return { confirmed: false, reason: "not_configured" };

    const candidates: Candidate[] = [];
    const formIds = formIdsForStep(step);

    if (formIds.length > 0) {
      const cutoff = Date.now() - WINDOW_MS;
      for (const formId of formIds) {
        const { submissions } = await fetchFormSubmissions({ formId, limit: 20 });
        for (const s of submissions) {
          const t = Date.parse(s.submittedAt);
          if (!s.id || !Number.isFinite(t) || t < cutoff) continue;
          candidates.push({
            ghlId: s.id,
            formId,
            formName: formNameById(formId),
            name: s.contactName,
            email: s.email,
            phone: s.phone,
            submittedAt: s.submittedAt,
          });
        }
      }
    } else {
      // Visibility Audit widget: no form record, so match a freshly created contact.
      const cutoff = Date.now() - CONTACT_WINDOW_MS;
      const { contacts } = await recentContacts(20);
      for (const c of contacts) {
        const t = Date.parse(c.createdAt);
        if (!c.id || !Number.isFinite(t) || t < cutoff) continue;
        candidates.push({
          ghlId: c.id,
          formId: "",
          formName: "Visibility Audit",
          name: c.name,
          email: c.email,
          phone: c.phone,
          submittedAt: c.createdAt,
        });
      }
    }

    if (candidates.length === 0) return { confirmed: false, reason: "no_matching_lead" };

    // Newest first, then skip anything we already recorded so a still-propagating
    // lead is not masked by an earlier one.
    candidates.sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : -1));

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const ids = candidates.map((c) => c.ghlId);
    const emails = candidates.map((c) => c.email).filter(Boolean);
    const { data: existingRows } = await supabaseAdmin
      .from("form_submissions")
      .select("ghl_id, email")
      .in("ghl_id", ids);
    const takenIds = new Set((existingRows ?? []).map((r) => r.ghl_id));

    // For the widget path there is no form record, so guard against attributing
    // someone else's recent form lead (e.g. Round Table) to the audit.
    const foreignEmails = new Set<string>();
    if (formIds.length === 0 && emails.length > 0) {
      const { data: otherRows } = await supabaseAdmin
        .from("form_submissions")
        .select("email, step")
        .in("email", emails);
      for (const r of otherRows ?? []) {
        if (r.email && r.step !== "visibility_audit") foreignEmails.add(r.email.toLowerCase());
      }
    }

    const match = candidates.find(
      (c) => !takenIds.has(c.ghlId) && !(c.email && foreignEmails.has(c.email.toLowerCase())),
    );
    if (!match) return { confirmed: true, reason: "already_recorded" };

    const { error } = await supabaseAdmin.from("form_submissions").insert({
      step,
      ghl_id: match.ghlId,
      form_id: match.formId,
      form_name: match.formName,
      contact_name: match.name,
      email: match.email,
      phone: match.phone,
      source: "confirmation",
      submitted_at: match.submittedAt || new Date().toISOString(),
    });
    if (error && !/duplicate|unique/i.test(error.message)) {
      console.error(`[submissions] insert failed: ${error.message}`);
      return { confirmed: false, reason: "store_failed" };
    }

    await supabaseAdmin.from("analytics_events").insert({
      type: "form_submit",
      path: data.path,
      cta: step,
      session_id: data.sessionId ?? null,
    });

    return { confirmed: true };
  });
